import {
  getMergedFieldValidation,
  getMergedFieldRules,
  getActiveConditionFields,
  getPolicyStatements,
  isFieldVisible,
  POLICY_SCHEMA,
  operatorNeedsValue,
  NUMERIC_OPERATORS,
  RANGE_OPERATORS,
  STATEMENT_FIELD_KEYS,
} from '../hooks/usePolicyMetadata';
import { principalObjectFromEntries, normalizePrincipalFormValue } from './principalTypes';
import { buildApprovalObject, pluginsArrayFromEntries } from './pluginConfig';

export function slugify(str) {
  return String(str || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function csvToArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function wildcardOrArray(value) {
  if (value === '*' || (Array.isArray(value) && value.length === 1 && value[0] === '*')) return ['*'];
  return csvToArray(value);
}

function coerceConditionValue(value) {
  if (typeof value === 'boolean' || typeof value === 'number') return value;
  if (Array.isArray(value)) return value.map(coerceConditionValue);
  const str = String(value ?? '').trim();
  if (str === 'true') return true;
  if (str === 'false') return false;
  if (str !== '' && Number.isFinite(Number(str)) && /^-?\d+(\.\d+)?$/.test(str)) {
    return Number(str);
  }
  return value;
}

/**
 * Condition evaluators that must emit:
 *   { "all": [{ "fact", "operator", "value" }] }
 * Contains, NotContains, between, not_between, and bool variants.
 * Everything else (StringEquals, NumericGreaterThan, …) stays IAM:
 *   { "StringEquals": { "request.prompt": "..." } }
 */
const ALL_FORMAT_OPERATOR_KEYS = new Set([
  'contains',
  'notcontains',
  'between',
  'not_between',
  'notbetween',
  'bool',
]);

function operatorKey(operator) {
  return String(operator || '')
    .trim()
    .replace(/[\s-]+/g, '')
    .toLowerCase();
}

function isAllFormatOperator(operator) {
  const key = operatorKey(operator);
  if (ALL_FORMAT_OPERATOR_KEYS.has(key)) return true;
  // not_between with underscore already covered; also accept notBetween style
  if (key === 'not_between') return true;
  return false;
}

function usesAllConditionFormat(conditionTemplateId, operator) {
  if (conditionTemplateId === 'timeRange') return true;
  return isAllFormatOperator(operator);
}

/** { all: [{ fact, operator, value }] } */
function buildAllCondition(rules) {
  const normalized = (rules || [])
    .filter((rule) => rule && rule.fact && rule.operator)
    .map((rule) => {
      const next = {
        fact: rule.fact,
        operator: rule.operator,
      };
      if (rule.value !== undefined) {
        next.value = coerceConditionValue(rule.value);
      }
      return next;
    });
  return normalized.length ? { all: normalized } : undefined;
}

/** IAM-style: { OperatorName: { "fact.path": value } } */
function mergeIamCondition(target, operator, fact, value) {
  if (!operator || !fact) return target;
  const next = target || {};
  next[operator] = {
    ...(next[operator] || {}),
    [fact]: coerceConditionValue(value),
  };
  return next;
}

/**
 * Normalize only all-format evaluators that were stored as IAM maps
 * (Contains / NotContains / between / not_between / bool) into { all: [...] }.
 * Leave StringEquals and other IAM maps unchanged.
 */
export function normalizeConditionFormat(condition) {
  if (!condition || typeof condition !== 'object') return condition;
  if (Array.isArray(condition.all)) {
    return buildAllCondition(condition.all);
  }

  const allRules = [];
  const iamRest = {};

  Object.entries(condition).forEach(([operator, factMap]) => {
    if (!factMap || typeof factMap !== 'object' || Array.isArray(factMap)) return;

    if (isAllFormatOperator(operator)) {
      Object.entries(factMap).forEach(([fact, value]) => {
        allRules.push({ fact, operator, value });
      });
      return;
    }

    iamRest[operator] = { ...(iamRest[operator] || {}), ...factMap };
  });

  if (!allRules.length) return Object.keys(iamRest).length ? iamRest : condition;

  const allCondition = buildAllCondition(allRules);
  if (!Object.keys(iamRest).length) return allCondition;
  return { ...iamRest, ...allCondition };
}

export function normalizeStatementCondition(statement) {
  if (!statement || typeof statement !== 'object') return statement;
  if (!statement.condition) return statement;
  const condition = normalizeConditionFormat(statement.condition);
  if (!condition) {
    const { condition: _omit, ...rest } = statement;
    return rest;
  }
  return { ...statement, condition };
}

function principalObject(value) {
  const entries = normalizePrincipalFormValue(value);
  return principalObjectFromEntries(entries);
}

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : value;
}

function resolveConditionValue(operator, formValues) {
  if (!operatorNeedsValue(operator)) return undefined;
  if (RANGE_OPERATORS.includes(operator)) {
    return [toNumber(formValues.valueMin), toNumber(formValues.valueMax)];
  }
  const raw = formValues.value;
  if (isAllFormatOperator(operator) && operatorKey(operator) === 'bool') {
    if (typeof raw === 'boolean') return raw;
    const str = String(raw ?? '').trim().toLowerCase();
    if (str === 'true') return true;
    if (str === 'false') return false;
    return Boolean(raw);
  }
  if (NUMERIC_OPERATORS.includes(operator)) {
    return toNumber(raw);
  }
  if (formValues.conditionTemplate === 'list' || (String(raw).includes(',') && operator === 'StringEquals')) {
    return csvToArray(raw);
  }
  return String(raw ?? '');
}

function buildConditionFromTemplate(conditionTemplateId, formValues) {
  const template = POLICY_SCHEMA.conditionTemplates[conditionTemplateId];
  if (!template?.conditionBuild || template.omitCondition) return undefined;

  // timeRange uses between evaluator → all format
  if (conditionTemplateId === 'timeRange') {
    return buildAllCondition([{
      fact: formValues.fact || 'system.hour',
      operator: 'between',
      value: [toNumber(formValues.valueMin), toNumber(formValues.valueMax)],
    }]);
  }

  const operator = formValues.operator;
  const fact = formValues.fact;
  if (!operator || !fact) return undefined;

  const value = resolveConditionValue(operator, formValues);
  if (value === undefined && operatorNeedsValue(operator)) return undefined;

  // Contains / NotContains / between / not_between / bool → all format
  if (usesAllConditionFormat(conditionTemplateId, operator)) {
    return buildAllCondition([{ fact, operator, value }]);
  }

  // StringEquals, NumericGreaterThan, RoleEquals, … → IAM map
  return mergeIamCondition(undefined, operator, fact, value);
}

function buildApproval(formValues) {
  if (formValues.effect !== 'Approve') return undefined;
  return buildApprovalObject(formValues.approvalRole);
}

function buildPlugins(formValues) {
  return pluginsArrayFromEntries(formValues.plugins);
}

function buildPolicyStatement(statementValues, policyLabel = '') {
  const sid =
    String(statementValues.sid || '').trim() ||
    slugify(policyLabel) ||
    'CustomRule';

  const statement = {
    sid,
    effect: statementValues.effect || 'Deny',
    action: wildcardOrArray(statementValues.action),
    resource: wildcardOrArray(statementValues.resource),
  };

  // Optional fields — omit when empty
  const principal = principalObject(statementValues.principal);
  if (principal) statement.principal = principal;

  const condition = buildConditionFromTemplate(statementValues.conditionTemplate, statementValues);
  if (condition && Object.keys(condition).length) statement.condition = condition;

  const approval = buildApproval(statementValues);
  if (approval) statement.approval = approval;

  const plugins = buildPlugins(statementValues);
  if (plugins?.length) statement.plugins = plugins;

  if (statementValues.threat && typeof statementValues.threat === 'object') {
    statement.threat = statementValues.threat;
  }

  return statement;
}

export function buildPolicyDocument(formValues) {
  const statements = getPolicyStatements(formValues).map((statementValues) => (
    buildPolicyStatement(statementValues, formValues.label)
  ));

  return {
    version: POLICY_SCHEMA.policyVersion,
    statements,
  };
}

export function buildPolicyPayload(formValues, meta = {}) {
  const statements = getPolicyStatements(formValues);
  const normalizedFormValues = {
    ...formValues,
    statements,
    conditionTemplate: statements[0]?.conditionTemplate || formValues.conditionTemplate,
  };

  return {
    label: String(formValues.label || '').trim(),
    description: String(formValues.description || '').trim(),
    categoryId: formValues.categoryId,
    conditionTemplate: normalizedFormValues.conditionTemplate,
    formValues: normalizedFormValues,
    policyDocument: buildPolicyDocument(normalizedFormValues),
    ...meta,
  };
}

function validateRule(fieldKey, value, rules, formValues) {
  const errors = [];
  const label = POLICY_SCHEMA.fields[fieldKey]?.label || fieldKey;

  if (rules.required && (value === '' || value === null || value === undefined)) {
    errors.push({ field: fieldKey, message: `${label} is required` });
    return errors;
  }

  if (rules.requiredWhen) {
    const shouldRequire = Object.entries(rules.requiredWhen).every(
      ([k, v]) => formValues[k] === v,
    );
    if (shouldRequire && !String(value ?? '').trim() && !(Array.isArray(value) && value.length)) {
      errors.push({ field: fieldKey, message: `${label} is required` });
      return errors;
    }
  }

  const str = String(value ?? '');
  if (rules.minLength && str.length < rules.minLength) {
    errors.push({ field: fieldKey, message: `${label} must be at least ${rules.minLength} characters` });
  }
  if (rules.maxLength && str.length > rules.maxLength) {
    errors.push({ field: fieldKey, message: `${label} must be at most ${rules.maxLength} characters` });
  }
  if (rules.pattern && str && !new RegExp(rules.pattern).test(str)) {
    errors.push({ field: fieldKey, message: `${label} has an invalid format` });
  }
  if (rules.min !== undefined && Number(value) < rules.min) {
    errors.push({ field: fieldKey, message: `${label} must be at least ${rules.min}` });
  }
  if (rules.max !== undefined && Number(value) > rules.max) {
    errors.push({ field: fieldKey, message: `${label} must be at most ${rules.max}` });
  }

  return errors;
}

function validatePolicyStatement(statementValues, categoryId, statementIndex, policyLabel = '') {
  const errors = [];
  const fieldPrefix = `statements.${statementIndex}`;

  ['effect', 'action', 'resource'].forEach((key) => {
    const rules = getMergedFieldValidation(key, categoryId);
    const field = POLICY_SCHEMA.fields[key];
    if (!field || !isFieldVisible(field, statementValues)) return;
    validateRule(key, statementValues[key], rules, statementValues).forEach((error) => {
      errors.push({ ...error, field: `${fieldPrefix}.${error.field}` });
    });
  });

  if (!String(statementValues.sid || '').trim() && !String(policyLabel || '').trim()) {
    errors.push({ field: `${fieldPrefix}.sid`, message: 'Statement ID or policy name is required' });
  }

  if (statementValues.effect === 'Approve') {
    const role = String(statementValues.approvalRole || '').trim();
    if (!role) {
      errors.push({
        field: `${fieldPrefix}.approvalRole`,
        message: 'Approval role is required when effect is Approve',
      });
    }
  }

  getActiveConditionFields(statementValues.conditionTemplate).forEach((field) => {
    const fieldDef = getMergedFieldRules(field.key, statementValues.conditionTemplate) || field;
    if (!isFieldVisible(fieldDef, statementValues)) return;
    if (field.key === 'value' && !operatorNeedsValue(statementValues.operator)) return;
    if (field.key === 'value' && RANGE_OPERATORS.includes(statementValues.operator)) return;
    if ((field.key === 'valueMin' || field.key === 'valueMax')
      && !RANGE_OPERATORS.includes(statementValues.operator)
      && statementValues.conditionTemplate !== 'timeRange') return;
    const template = POLICY_SCHEMA.conditionTemplates[statementValues.conditionTemplate];
    if (template?.omitCondition) return;
    const rules = {
      ...getMergedFieldValidation(field.key, categoryId),
      ...(fieldDef?.validation || {}),
    };
    validateRule(field.key, statementValues[field.key], rules, statementValues).forEach((error) => {
      errors.push({ ...error, field: `${fieldPrefix}.${error.field}` });
    });
  });

  ['approvalRole', 'plugins'].forEach((key) => {
    const field = POLICY_SCHEMA.fields[key];
    if (!field || !isFieldVisible(field, statementValues)) return;
    const rules = getMergedFieldValidation(key, categoryId);
    validateRule(key, statementValues[key], rules, statementValues).forEach((error) => {
      errors.push({ ...error, field: `${fieldPrefix}.${error.field}` });
    });
  });

  return errors;
}

export function validatePolicyForm(formValues, categoryId) {
  const errors = [];

  ['label', 'categoryId'].forEach((key) => {
    const rules = getMergedFieldValidation(key, categoryId);
    const field = POLICY_SCHEMA.fields[key];
    if (!field || !isFieldVisible(field, formValues)) return;
    errors.push(...validateRule(key, formValues[key], rules, formValues));
  });

  const statements = getPolicyStatements(formValues);
  if (!statements.length) {
    errors.push({ field: 'statements', message: 'At least one statement is required' });
    return errors;
  }

  statements.forEach((statementValues, index) => {
    errors.push(...validatePolicyStatement(statementValues, categoryId, index, formValues.label));
  });

  return errors;
}

export { STATEMENT_FIELD_KEYS };
